import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@mdvip/shared/components/ui/card';
import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';
import { CurrentBalanceMetric } from './CurrentBalanceMetric';
import { NextAmountDueMetric } from './NextAmountDueMetric';
import { PaymentPlanMetric } from './PaymentPlanMetric';

interface PaymentSummaryCardProps {
  className?: string;
}

/**
 * Payment Center's "Payment summary" card — the design's shared 3-metric shell (Current Balance,
 * Next Amount Due, Payment Plan), responsive between a narrow `BalanceHero` layout and a wide
 * grouped 3-column card. The split is driven by a `@5xl` **container** query against the nearest
 * `@container` ancestor — `PaymentCenterPage`'s full-width grid wrapper, not this card's own
 * rendered column (a container query can only resolve against an ancestor, never a value scoped to
 * the querying element's own box). So the `@5xl` threshold reflects the wrapper's width, not the
 * narrower 8/12 slot this card actually occupies once the wide layout kicks in.
 *
 * This is a pure layout shell — each metric owns its own data hook (if any), its own
 * loading/error/none/data ladder, and both responsive renderings, mounted here as two instances
 * of the same component (`layout="narrow" | "wide"`). Both layouts render simultaneously via CSS
 * visibility, so both instances of every metric are always mounted. Positional padding for the
 * wide 3-column grid is owned here, not by the metrics, since only this layout knows which column
 * a metric sits in.
 *
 * All three metrics are wired to live data.
 */
export function PaymentSummaryCard({ className }: PaymentSummaryCardProps) {
  const { t } = useTranslation('members');

  return (
    <div data-slot="card-payment-summary" className={cn('min-w-0 @5xl:h-full', className)}>
      {/* narrow: BalanceHero layout */}
      <Card data-slot="card-payment-summary-narrow" className="!gap-0 overflow-hidden !p-0 @5xl:hidden">
        <div className="grid grid-cols-2 divide-x divide-border">
          <CurrentBalanceMetric layout="narrow" className="min-w-0 p-6" />
          <NextAmountDueMetric layout="narrow" className="min-w-0 p-6" />
        </div>
        <PaymentPlanMetric layout="narrow" className="border-t border-border p-6" />
      </Card>

      {/* wide: grouped "Payment summary" card */}
      <Card data-slot="card-payment-summary-wide" className="hidden @5xl:flex @5xl:h-full @5xl:flex-col">
        <CardHeader>
          <CardTitle className="text-2xl">{t('payments.paymentSummary.title')}</CardTitle>
          <CardDescription>{t('payments.paymentSummary.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 divide-x divide-border">
            <CurrentBalanceMetric layout="wide" className="pb-4 sm:pb-0 sm:pr-6" />
            <NextAmountDueMetric layout="wide" className="py-4 sm:py-0 sm:px-6" />
            <PaymentPlanMetric layout="wide" className="pt-4 sm:pt-0 sm:pl-6" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
